import {
  START_URL, VALID_PLAN_CODES, promoCodeFor, bonusCodeFor, sweatshirtBlocked,
} from '../config';

// Turns the URL Steps 1-2 hand over (static page, ?dt=&est=&gift=&pln=&promo=
// &bonus=) into this app's flow state. Pure, so it can be tested without a
// browser; App.js supplies the saved state and a promo/bonus storage reader.
//
// Rules:
//  * The URL beats whatever sessionStorage remembered. With a valid ?dt= it is
//    a fresh handoff, so the answers it carries (est, gift, pln) are replaced
//    outright, including ones it leaves out — a stale saved gift/estimate must
//    not survive into a new order. Saved delivery details are left alone.
//  * Nothing from the URL is trusted: dt must be a real calendar date, pln one
//    of the 16 plan codes, promo/bonus only codes that have a real Recurly
//    add-on behind them (see promoCodeFor / bonusCodeFor in config.js).
//  * The plan is whatever ?pln= says. It is never worked out from the date
//    here: that rule belongs to the static page that hands the customer over.
//    No valid pln means no plan, and App.js sends them back to start.
//  * promo/bonus may also come from a cookie/session value a marketing page
//    set on this origin, but only when the URL doesn't carry them at all.

export const EMPTY_FLOW = {
  audience: 'self', wedDate: '', wedDateEstimated: false,
};

const GIFT_VALUES = ['yes', '1', 'true'];

function isRealDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

export function flowStateFromUrl(search, saved, stored = () => '') {
  const p = new URLSearchParams(search);
  const next = { ...EMPTY_FLOW, ...(saved || {}) };

  const dt = p.get('dt') || '';
  const handoff = isRealDate(dt);
  if (handoff) {
    next.wedDate = dt;
    next.wedDateEstimated = p.get('est') === '1';
    next.planCode = '';
  }

  if (handoff || p.has('gift')) {
    next.audience = GIFT_VALUES.includes((p.get('gift') || '').toLowerCase()) ? 'gift' : 'self';
  }

  const pln = (p.get('pln') || '').toLowerCase();
  if (VALID_PLAN_CODES.includes(pln)) {
    next.planCode = pln;
  } else if (!VALID_PLAN_CODES.includes(next.planCode)) {
    next.planCode = ''; // also drops a stale or corrupted saved plan
  }

  const rawPromo = p.has('promo') ? p.get('promo') : stored('promo');
  next.appliedCoupon = promoCodeFor(rawPromo);

  const rawBonus = (p.has('bonus') ? p.get('bonus') : stored('bonus')) || '';
  next.sweatshirtBlocked = sweatshirtBlocked(next.planCode, rawBonus);
  next.bonusCode = bonusCodeFor(next.planCode, rawBonus);

  return next;
}

// The "Start over" address: the start page with this app's answers dropped but
// everything else (utm_*, promo, bonus, looking_for, …) kept.
export function startOverUrl(search, startUrl = START_URL) {
  try {
    const url = new URL(startUrl);
    const params = new URLSearchParams(search);
    ['step', 'dt', 'wd', 'est', 'gift', 'pln', 'pln2'].forEach((k) => params.delete(k));
    url.search = params.toString();
    return url.toString();
  } catch { return startUrl; }
}
